// frontend/src/mobile/MobileInvoiceSheet.jsx
import { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { ArrowLeftRight, FileText } from "lucide-react";
import InvoicePreviewDialog from "@/components/InvoicePreviewDialog";
import { API, apiFetch } from "@/utils/api";
import { formatCurrency, formatCurrencyIn, formatDateShort, formatSignedCurrency } from "@/utils/format";
import { invoiceStatusMeta, installmentLine } from "@/utils/invoiceStatus";
import { formatInvoiceLabel } from "@/utils/invoiceFormat";

const LINE_TONE = { rose: "text-rose-600", amber: "text-amber-700" };

function payText(inv, kind) {
  const ps = inv.payment_status;
  if (kind === "emitted") {
    if (inv.status === "draft") return "Borrador: no cuenta como pendiente de cobro. Se emite desde escritorio.";
    if (ps === "paid" || ps === "overpaid") return "Cobrada al conciliarse con un movimiento de tu banco.";
    if (ps === "compensated") return "Compensada con una rectificativa (detalle en escritorio).";
    return "Sin cobrar. Se marcará «Cobrada» automáticamente al conciliar el ingreso en tu banco — no hay marcado manual.";
  }
  if (inv.status === "pending") return "Pendiente de revisar y contabilizar (se hace en escritorio).";
  if (ps === "paid" || ps === "overpaid") return "Pagada al conciliarse con un movimiento de tu banco.";
  return "Contabilizada. Se marcará «Pagada» automáticamente al conciliar el cargo en tu banco — no hay marcado manual.";
}

export default function MobileInvoiceSheet({ invoice, kind, onClose }) {
  const [txs, setTxs] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const linkSeq = useRef(0);

  useEffect(() => {
    setTxs(null);
    if (!invoice) return;
    const seq = ++linkSeq.current;
    (async () => {
      try {
        const r = await apiFetch(`${API}/invoices/${invoice.invoice_id}/transactions`);
        if (seq !== linkSeq.current) return;
        setTxs(r.ok ? (await r.json()).transactions ?? [] : []);
      } catch {
        if (seq === linkSeq.current) setTxs([]);
      }
    })();
  }, [invoice]);

  if (!invoice) return null;
  const meta = invoiceStatusMeta(invoice, kind);
  const isForeign = (invoice.currency ?? "EUR") !== "EUR";
  const irpf = invoice.irpf_amount ?? 0;
  const plazos = kind === "emitted" ? installmentLine(invoice) : null;
  const pdfUrl = kind === "emitted"
    ? `${API}/invoices/emitted/${invoice.invoice_id}/pdf`
    : `${API}/invoices/received/${invoice.invoice_id}/pdf`;

  return (
    <Sheet open={!!invoice} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="bottom" data-testid="minv-sheet" className="max-h-[85%] p-0 rounded-t-2xl flex flex-col">
        <SheetHeader className="shrink-0 px-4 pt-3 pb-0 text-left">
          <SheetTitle className="flex items-center gap-2.5 font-mono text-[17px]">
            {kind === "emitted" ? formatInvoiceLabel(invoice.series, invoice.invoice_number) : invoice.invoice_number}
            <Badge variant="secondary" className={meta.cls}>{meta.label}</Badge>
          </SheetTitle>
          <SheetDescription className="sr-only">Detalle de la factura</SheetDescription>
        </SheetHeader>

        <div className="overflow-y-auto px-4 pb-4 pt-2 flex flex-col gap-2.5">
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
            <p className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
              {kind === "emitted" ? "Cliente" : "Proveedor"}
            </p>
            <p className="mt-0.5 text-sm font-semibold text-slate-900">
              {kind === "emitted" ? invoice.client_name : invoice.supplier_name}
            </p>
            {invoice.concept && <p className="mt-0.5 text-xs text-slate-500">{invoice.concept}</p>}
            <div className="mt-2.5 flex gap-5">
              <div>
                <p className="text-[9.5px] font-semibold uppercase tracking-wider text-slate-400">
                  {kind === "emitted" ? "Emisión" : "Fecha"}
                </p>
                <p className="font-mono text-xs text-slate-900">{formatDateShort(invoice.invoice_date)}</p>
              </div>
              <div>
                <p className="text-[9.5px] font-semibold uppercase tracking-wider text-slate-400">
                  {kind === "emitted" ? "Vencimiento" : "Nº proveedor"}
                </p>
                <p className="font-mono text-xs text-slate-900">
                  {kind === "emitted" ? formatDateShort(invoice.due_date) : (invoice.invoice_number ?? "—")}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl px-3 divide-y divide-slate-100">
            <Row label="Base imponible" value={fmtIn(invoice, invoice.base_amount)} />
            <Row label={`IVA (${invoice.vat_rate ?? 21} %)`} value={fmtIn(invoice, invoice.vat_amount)} />
            {irpf !== 0 && (
              <Row label={`Retención IRPF (−${invoice.irpf_rate ?? ""} %)`} value={`-${fmtIn(invoice, Math.abs(irpf))}`} valueCls="text-rose-600" />
            )}
            <Row label="Total" value={fmtIn(invoice, invoice.total_amount)} strong />
            {isForeign && invoice.total_amount_eur != null && (
              <Row label="EUR real (conciliación)" value={formatCurrency(invoice.total_amount_eur)} />
            )}
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
            <p className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
              {kind === "emitted" ? "Cobro" : "Pago"}
            </p>
            <p className="mt-1 text-xs leading-relaxed text-slate-600">{payText(invoice, kind)}</p>
            {meta.line && (
              <p className={`mt-1 text-[11.5px] font-semibold ${LINE_TONE[meta.lineTone] ?? "text-amber-700"}`}>{meta.line}</p>
            )}
            {plazos && <p className="mt-1 text-[11.5px] text-slate-500">{plazos}</p>}
            {isForeign && invoice.total_amount_eur == null && (
              <p className="mt-1 text-[11.5px] text-amber-700">Divisa extranjera: el EUR real llega con la conciliación.</p>
            )}
            {txs?.length > 0 && (
              <div data-testid="minv-sheet-link" className="mt-2 flex flex-col gap-1.5">
                {txs.map((t) => (
                  <div key={t.transaction_id}
                    className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 min-h-[44px]">
                    <ArrowLeftRight className="w-3.5 h-3.5 shrink-0 text-emerald-600" strokeWidth={1.5} />
                    <span className="flex-1 min-w-0 truncate text-xs font-medium text-emerald-800">
                      {t.description}
                    </span>
                    <span className="font-mono text-xs tabular-nums text-emerald-700">{formatSignedCurrency(t.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {(kind === "emitted" || invoice.pdf_path || invoice.document_id) && (
            <button data-testid="minv-sheet-pdf" onClick={() => setPdfOpen(true)}
              className="w-full min-h-[48px] flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white text-[13.5px] font-semibold text-slate-900">
              <FileText className="w-4 h-4" strokeWidth={1.5} />
              {kind === "emitted" ? "Ver PDF de la factura" : "Ver justificante / PDF"}
            </button>
          )}
          <p className="pb-1 text-center text-[11px] text-slate-400">
            Consulta en móvil — la edición completa queda en escritorio.
          </p>
        </div>
        <InvoicePreviewDialog url={pdfUrl} open={pdfOpen} onOpenChange={setPdfOpen} hideTrigger />
      </SheetContent>
    </Sheet>
  );
}

function fmtIn(inv, amount) {
  const cur = inv.currency ?? "EUR";
  return cur === "EUR" ? formatCurrency(amount ?? 0) : formatCurrencyIn(amount ?? 0, cur);
}

function Row({ label, value, strong = false, valueCls = "" }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5">
      <span className={`text-xs ${strong ? "font-semibold text-slate-900" : "text-slate-500"}`}>{label}</span>
      <span className={`font-mono tabular-nums ${strong ? "text-base font-semibold" : "text-xs font-medium"} ${valueCls || "text-slate-900"}`}>
        {value}
      </span>
    </div>
  );
}
